import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  Shield, Lock, Key, Server, FileCheck, Eye,
  CheckCircle, AlertTriangle
} from 'lucide-react'
import SEO, { schemas } from '../components/SEO'

const SECURITY_EMAIL = 'security@cmdesk.help'

// Same order as `features` in the locale file.
const featureIcons = [Lock, Key, Server, FileCheck]

interface Feature {
  title: string
  description: string
}

export default function Security() {
  const { t } = useTranslation(['security', 'common'])
  const features = (t('features', { returnObjects: true }) as Feature[]).map((feature, i) => ({
    ...feature,
    icon: featureIcons[i],
  }))
  const certifications = t('certifications.items', { returnObjects: true }) as string[]

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}

      className="pt-32 pb-24"
    >
      <SEO
        title={t('seo.title')}
        description={t('seo.description')}
        keywords={t('seo.keywords')}
        canonical="/security"
        structuredData={schemas.breadcrumb([{ name: t('common:pages.home'), url: '/' }, { name: t('common:pages.security'), url: '/security' }])}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm font-medium mb-4">
            <Shield className="w-4 h-4" />
            {t('hero.eyebrow')}
          </span>
          <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
            {t('hero.titleStart')}{' '}
            <span className="text-gradient">{t('hero.titleHighlight')}</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t('hero.text')}
          </p>
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {features.map((feature, _index) => (
            <div key={feature.title} className="glass rounded-2xl p-6 text-center">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <feature.icon className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
              <p className="text-gray-400 text-sm">{feature.description}</p>
            </div>
          ))}
        </motion.div>

        {/* Security Details */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-8"
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <Eye className="w-6 h-6 text-primary" />
              {t('encryption.title')}
            </h2>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">{t('encryption.e2e.title')}</span>
                  <p className="text-gray-400 text-sm">{t('encryption.e2e.text')}</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">TLS 1.3</span>
                  <p className="text-gray-400 text-sm">{t('encryption.tls.text')}</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">{t('encryption.pfs.title')}</span>
                  <p className="text-gray-400 text-sm">{t('encryption.pfs.text')}</p>
                </div>
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-8"
          >
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-accent" />
              {t('measures.title')}
            </h2>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">{t('measures.bruteForce.title')}</span>
                  <p className="text-gray-400 text-sm">{t('measures.bruteForce.text')}</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">{t('measures.anomaly.title')}</span>
                  <p className="text-gray-400 text-sm">{t('measures.anomaly.text')}</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">{t('measures.audit.title')}</span>
                  <p className="text-gray-400 text-sm">{t('measures.audit.text')}</p>
                </div>
              </li>
            </ul>
          </motion.div>
        </div>

        {/* Certifications */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass rounded-2xl p-8 text-center"
        >
          <h2 className="text-2xl font-bold text-white mb-8">{t('certifications.title')}</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {certifications.map((cert) => (
              <div key={cert} className="px-6 py-3 bg-white/5 rounded-full">
                <span className="text-white font-medium">{cert}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <h2 className="text-2xl font-bold text-white mb-4">{t('report.title')}</h2>
          <p className="text-gray-400 mb-6">
            {t('report.text')}
          </p>
          <a
            href={`mailto:${SECURITY_EMAIL}`}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary-dark text-white font-medium rounded-xl transition-colors"
          >
            {SECURITY_EMAIL}
          </a>
        </motion.div>
      </div>
    </motion.div>
  )
}
